import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AccordionBody, AccordionHeader, AccordionItem, Input, Label } from "reactstrap";
import FormInput from "src/components/Common/Form/FormInput";
import FormSelect from "src/components/Common/Form/FormSelect";
import FormTextarea from "src/components/Common/Form/FormTextarea";
import FormUpload from "src/components/Common/Form/FormUpload";
import { FILE_TYPE, FORM_TYPE, UPLOAD_TYPE } from "src/constant/common";
import { FORM_NAME } from "src/constant/common";
import { useRecoilValue } from "recoil";
import { uploadingFile } from "src/recoil/atoms/global";

const SIMULATION_MODES = [
  {
    label: "sim - ASCA1_LandingSimulator",
    value: 0,
  },
  {
    label: "mcs - Monte Carlo Simulation",
    value: 1,
  },
];

const MainPart = ({
  register,
  errors,
  control,
  fields,
  versions,
  handleUpdateAttachments,
  setIsChecked,
  isChecked,
  setIsUpload,
  simData,
  formType,
  handleRedirectURLSimParent,
  isSimModeMCS,
  setIsSimModeMCS,
}) => {
  const { t } = useTranslation("gnc-simulator-6dof");
  const upLoading = useRecoilValue(uploadingFile);
  const { id: simId } = useParams();
  const isSimCopy = formType === FORM_TYPE.COPY;
  const isSimRetry = formType === FORM_TYPE.EDIT;
  const getSimId = (isSimCopy, isSimRetry, currentSimId, simData) => {
    if (isSimCopy) {
      return currentSimId;
    }
    if (isSimRetry) {
      return simData?.parent?.id;
    }
    return null;
  };

  const getSimName = (isSimCopy, isSimRetry, simData) => {
    if (isSimCopy) {
      return simData?.currentName;
    }
    if (isSimRetry) {
      return simData?.parent?.name;
    }
    return null;
  };

  const handleChangeSimMode = (value) => {
    if (value === 1) {
      setIsSimModeMCS(true);
    } else {
      setIsSimModeMCS(false);
    }
  };
  useEffect(() => {
    if (isSimRetry && !simData?.parent?.id) {
      setIsChecked(true);
    }
  }, []);

  return (
    <AccordionItem>
      <AccordionHeader targetId="1" className="mt-0 fw-bold">
        {t("new.main.title")}
      </AccordionHeader>
      <AccordionBody accordionId="1">
        <div className="row">
          <div className="col-md-6">
            <FormInput
              register={register}
              label={t(`new.main.${FORM_NAME.NAME}`)}
              name={FORM_NAME.NAME}
              placeholder={t("new.field_placeholder", { field: t(`new.main.${FORM_NAME.NAME}`) })}
              classInput="width-by-percent-80"
              errors={errors?.[FORM_NAME.NAME]?.message}
              requiredMessage={t("new.field_required", { field: t(`new.main.${FORM_NAME.NAME}`) })}
              required
              maxLength={255}
              maxLengthMessage={t("new.field_maxlength", { field: t(`new.main.${FORM_NAME.NAME}`) })}
            />
            <FormSelect
              label={t(`new.main.${FORM_NAME.TYPE}`)}
              name={FORM_NAME.TYPE}
              placeholder={t("new.select_field_placeholder", { field: t(`new.main.${FORM_NAME.TYPE}`) })}
              classInput="width-by-percent-80"
              control={control}
              errors={errors?.[FORM_NAME.TYPE]?.message}
              required
              options={SIMULATION_MODES || []}
              requiredMessage={t("new.field_required", { field: t(`new.main.${FORM_NAME.TYPE}`) })}
              onChangeExtra={(value) => handleChangeSimMode(value)}
              disabled={isSimCopy || isSimRetry}
            />

            <FormSelect
              label={t(`new.main.${FORM_NAME.VERSION}`)}
              name={FORM_NAME.VERSION}
              placeholder={t("new.select_field_placeholder", { field: t(`new.main.${FORM_NAME.VERSION}`) })}
              classInput="width-by-percent-80"
              control={control}
              errors={errors?.[FORM_NAME.VERSION]?.message}
              required
              options={versions || []}
              requiredMessage={t("new.field_required", { field: t(`new.main.${FORM_NAME.VERSION}`) })}
            />

            <FormTextarea
              register={register}
              label={t(`new.main.${FORM_NAME.NOTE}`)}
              name={FORM_NAME.NOTE}
              placeholder={t("new.main.note_placeholder")}
              classInput="width-by-percent-80"
              rows={4}
            />
          </div>

          <div className="col-md-6">
            <div>
              <Label className="position-relative required-input">{t("new.main.params_series")}</Label>

              {fields?.map((param, index) => (
                <div className="d-flex gap-2" key={param.id}>
                  <div className="width-by-percent-80">
                    <FormUpload
                      register={register}
                      files={param.parameter_path}
                      name={`series.${index}.${FORM_NAME.PARAMETER_PATH}`}
                      handleUpdateAttachments={(attachments) => handleUpdateAttachments(attachments, index, FORM_NAME.PARAMETER_PATH)}
                      onUpload={setIsUpload}
                      isMultiple={false}
                      useNewApiUpload
                      uploadType={UPLOAD_TYPE["GNC_6DOF"]}
                      isMappingFields
                      errorMessage={errors?.series?.[index]?.[FORM_NAME.PARAMETER_PATH]?.message}
                      validate={(files) => files.length > 0 || t("new.required_attachments")}
                      isUploading={upLoading}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="aerodynamic-coefficients">
              <Label className="position-relative required-input">{`${t("new.main.aerodynamic_coefficients")} ${FILE_TYPE.XLSX}`}</Label>
              {fields?.map((param, index) => (
                <div className="d-flex gap-2" key={param.id}>
                  <div className="width-by-percent-80">
                    <FormUpload
                      register={register}
                      files={param.aerodynamic_coefficients_path}
                      name={`series.${index}.${FORM_NAME.AERODYNAMIC_COEFFICIENTS_PATH}`}
                      handleUpdateAttachments={(attachments) =>
                        handleUpdateAttachments(attachments, index, FORM_NAME.AERODYNAMIC_COEFFICIENTS_PATH)
                      }
                      onUpload={setIsUpload}
                      isMultiple={false}
                      useNewApiUpload
                      fileTypeAllow={[FILE_TYPE.XLSX]}
                      uploadType={UPLOAD_TYPE["GNC_6DOF"]}
                      errorMessage={errors?.series?.[index]?.[FORM_NAME.AERODYNAMIC_COEFFICIENTS_PATH]?.message}
                      validate={(files) => files.length > 0 || t("new.required_attachments")}
                      isUploading={upLoading}
                    />
                  </div>
                </div>
              ))}
            </div>

            <FormInput
              register={register}
              type="number"
              label={t(`new.main.${FORM_NAME.MAIN.SIMULATION_TIME}`)}
              name={`main.${FORM_NAME.MAIN.SIMULATION_TIME}`}
              classInput="width-by-percent-40"
              placeholder={t(`new.main.${FORM_NAME.MAIN.SIMULATION_TIME}_placeholder`)}
              errors={errors?.main?.[FORM_NAME.MAIN.SIMULATION_TIME]?.message}
              requiredMessage={t("new.field_required", { field: t(`new.main.${FORM_NAME.MAIN.SIMULATION_TIME}`) })}
              required
              valueGreaterThanZero
              valueGreaterThanZeroMessage={t("new.field_min", { field: t(`new.main.${FORM_NAME.MAIN.SIMULATION_TIME}`) })}
              maxLength={255}
              maxLengthMessage={t("new.field_maxlength", { field: t(`new.main.${FORM_NAME.MAIN.SIMULATION_TIME}`) })}
              unit="s"
            />
            {isSimModeMCS && (
              <FormInput
                register={register}
                type="number"
                label={t(`new.MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`)}
                name={`MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`}
                classInput="width-by-percent-40"
                placeholder={t("new.field_placeholder", { field: t(`new.MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`) })}
                errors={errors?.MCS?.[FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS]?.message}
                requiredMessage={t("new.field_required", { field: t(`new.MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`) })}
                required
                valueGreaterThanZero
                valueGreaterThanZeroMessage={t("new.field_min", { field: t(`new.MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`) })}
                maxLength={255}
                maxLengthMessage={t("new.field_maxlength", { field: t(`new.MCS.${FORM_NAME.MCS.NUMBER_OF_MCS_SIM_RUNS}`) })}
              />
            )}

            <div>
              <Label className="position-relative">{t("new.main.reference_history")}</Label>
              {isSimCopy || (isSimRetry && simData?.parent?.id) ? (
                <React.Fragment>
                  <p>
                    {t("new.main.simulation_copy")}{" "}
                    <button
                      type="button"
                      className="link-primary bg-transparent border-0 p-0"
                      onClick={() => handleRedirectURLSimParent(getSimId(isSimCopy, isSimRetry, simId, simData))}
                    >
                      SimID {getSimId(isSimCopy, isSimRetry, simId, simData)}
                    </button>{" "}
                    ({getSimName(isSimCopy, isSimRetry, simData)})
                  </p>
                  <div className="process-as-root-sim d-flex align-item-center">
                    <Input
                      id="process_as_root_sim_checkbox"
                      name="process_as_root_sim_checkbox"
                      type="checkbox"
                      onClick={() => setIsChecked(!isChecked)}
                    />
                    <Label for="process_as_root_sim_checkbox" className="fw-normal checkbox-label">
                      {t("new.main.process_as_root_sim")}
                    </Label>
                  </div>
                </React.Fragment>
              ) : (
                <p>{t("new.main.root_simulation")}</p>
              )}
            </div>
          </div>
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

export default MainPart;
